import moment from "moment";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Image, StyleSheet } from "react-native";
import { Button, Caption, Headline, Paragraph, TextInput, useTheme } from "react-native-paper";
import images from "@styles/images";
import { spacing } from "@styles/spacing";
import { useDesktopMediaQuery } from "@media-query";
import { Block } from "@block";
import { QueueSpacing, StackSpacing } from "@components/spacing";
import { testProps } from "app/shared/util/test-props";
import { ConvertCurrencyToVietnamese, formatCurrency } from "app/shared/util/format-currency";

interface HeaderSuccessTitleProps {
  amount?: string;
  headlineText?: string;
  headlineTwoText?: string;
}

export const HeaderSuccessTitleNone = (props: HeaderSuccessTitleProps) => {
  const { fonts } = useTheme();
  const { t } = useTranslation();

  const { headlineText = t("COMMON_GIAO_DICH_THANH_CONG") } = props;
  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        textHeaderNumber: { ...fonts.semibold, textAlign: "center", textTransform: "uppercase" },
        textDate: {
          textTransform: "capitalize",
          ...fonts.medium,
        },
      }),
    [fonts.medium, fonts.semibold],
  );

  return (
    <Block center>
      <Image source={images.logoPVCB_bg_white} style={{ width: 196, height: 100, resizeMode: "contain" }} />
      <StackSpacing size="medium" />
      <Headline style={styles.textHeaderNumber} {...testProps("successTxt")}>
        {headlineText}
      </Headline>
      <StackSpacing size="medium" />
      <Paragraph style={styles.textDate}>{moment().format("hh:mm - DD/MM/YYYY")}</Paragraph>
    </Block>
  );
};

export const HeaderSuccessTitle = (props: HeaderSuccessTitleProps) => {
  const { fonts, colors } = useTheme();
  const { t } = useTranslation();
  const convertCurrencyToVietnamese = new ConvertCurrencyToVietnamese();

  const { amount = "0", headlineText = t("COMMON_GIAO_DICH_THANH_CONG"), headlineTwoText } = props;
  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        textHeaderNumber: { ...fonts.semibold, textAlign: "center", textTransform: "uppercase" },
        textHeaderMoney: { ...fonts.semibold, textAlign: "center", color: colors.accent },
        textHeaderCharacter: { fontStyle: "italic", ...fonts.medium, color: colors.blackText },
        textDate: {
          textTransform: "capitalize",
          ...fonts.medium,
        },
      }),
    [colors.accent, colors.blackText, fonts.medium, fonts.semibold],
  );

  const getNumberAmount = () => {
    if (amount && amount.length > 0 && Number.isInteger(Number(amount))) {
      return formatCurrency(amount);
    }
    return "0";
  };

  const getCharacterAmount = () => {
    if (amount && amount.length > 0 && Number.isInteger(Number(amount))) {
      return convertCurrencyToVietnamese.convert(Number(amount));
    }
    return "0 đồng";
  };

  return (
    <Block center>
      <Image source={images.logoPVCB_bg_white} style={{ width: 196, height: 100, resizeMode: "contain" }} />
      <StackSpacing size="medium" />
      <Headline style={styles.textHeaderNumber} {...testProps("successTxt")}>
        {headlineText}
      </Headline>
      <StackSpacing size="medium" />
      <>{headlineTwoText && <Headline style={styles.textHeaderNumber}>{headlineTwoText}</Headline>}</>
      <Headline style={styles.textHeaderMoney}>{getNumberAmount()}</Headline>
      <Caption style={styles.textHeaderCharacter}>{t("COMMON_BANG_CHU") + ": " + getCharacterAmount()}</Caption>
      <StackSpacing size="small" />
      <Paragraph style={styles.textDate}>{moment().format("hh:mm - DD/MM/YYYY")}</Paragraph>
    </Block>
  );
};

export const RowInputColumn = ({ name = "", value = "" }) => {
  const { colors, fonts } = useTheme();
  const isDesktop = useDesktopMediaQuery();
  const textInputCommonStyles: object = {
    dense: true,
    style: { color: colors.accent, fontWeight: "bold", textShadowColor: "black", backgroundColor: colors.surface },
    theme: { colors: { primary: colors.accent, text: colors.accent } },
    outlineColor: "transparent",
    editable: false,
  };

  return (
    <React.Fragment>
      <Block w={isDesktop ? 600 : "100%"} middle row={isDesktop} center={isDesktop}>
        <Block w={isDesktop ? 180 : "100%"} ph={isDesktop ? 0 : spacing.medium}>
          <Paragraph style={{ ...fonts.medium }}>{name}</Paragraph>
        </Block>
        <Block w={isDesktop ? spacing.macro : 0} />
        <Block flex={1}>
          <TextInput autoCompleteType={undefined} multiline {...textInputCommonStyles} value={value} />
        </Block>
      </Block>
      <StackSpacing size="medium" />
    </React.Fragment>
  );
};

interface ButtonRowProps {
  onPressGoHome: Function;
  onPressGoBack: Function;
}
export const ButtonRow = (props: ButtonRowProps) => {
  const { onPressGoHome, onPressGoBack } = props;
  const { colors } = useTheme();
  const { t } = useTranslation();

  return (
    <Block row middle pv={spacing.huge}>
      <Button mode="contained" style={{ backgroundColor: colors.backgroundForm }} onPress={() => onPressGoHome()}>
        {t("FUNC_TRANG_CHU")}
      </Button>
      <QueueSpacing size="large" />
      <Button mode="contained" style={{ backgroundColor: colors.primary }} onPress={() => onPressGoBack()}>
        {t("COMMON_GIAO_DICH_KHAC")}
      </Button>
    </Block>
  );
};

interface ButtonFormActionsProps {
  onPressPrint: Function;
  onPressShare: Function;
}
export const ButtonFormActions = (props: ButtonFormActionsProps) => {
  const { onPressPrint, onPressShare } = props;
  const { colors } = useTheme();
  const isDesktop = useDesktopMediaQuery();
  const { t } = useTranslation();
  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        buttonIconSmall: { fontSize: 10, color: colors.whiteText },
      }),
    [colors.whiteText],
  );
  return (
    <Block center ph={isDesktop ? spacing.galactic * 2 : spacing.large}>
      <Block row>
        <Button
          mode="contained"
          style={{ backgroundColor: colors.accent, width: 110 }}
          labelStyle={styles.buttonIconSmall}
          onPress={() => onPressPrint()}>
          {t("COMMON_IN_BIEN_LAI")}
        </Button>
        <QueueSpacing size="large" />
        <Button
          style={{ backgroundColor: colors.accent, width: 110 }}
          mode="contained"
          labelStyle={styles.buttonIconSmall}
          onPress={() => onPressShare()}>
          {t("COMMON_LUU_ANH")}
        </Button>
      </Block>
    </Block>
  );
};
